import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Send, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/time";

const STATUS = { open: "En attente", answered: "Répondu", closed: "Clôturé" };

/** Support inbox: a conversation with the Metazik team. Signed-in users see their
 *  conversations; anyone (even someone who cannot sign in) can send a message. */
export default function SupportPage() {
  const { user, booted } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null);
  const [composing, setComposing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/support/tickets"); setTickets(data); }
    catch { setTickets([]); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!booted) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="support-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to={user ? "/" : "/auth"} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <h1 className="text-3xl font-black font-display flex items-center gap-3"><LifeBuoy className="w-7 h-7 text-[#FF0050]" /> Support</h1>
        <p className="text-sm text-zinc-400 mt-1">Une question, un problème de paiement, un compte à récupérer ? Écris à l'équipe Metazik.</p>
        <p className="text-sm text-zinc-400 mt-2">
          La réponse est peut-être déjà dans les <Link to="/faq" className="underline text-white" data-testid="faq-link-support">questions fréquentes</Link>{" "}
          ou les <Link to="/terms" className="underline">conditions d'utilisation</Link>.
        </p>

        {!user ? (
          <ContactForm />
        ) : active ? (
          <Thread id={active} onBack={() => { setActive(null); load(); }} />
        ) : composing ? (
          <NewTicket onCancel={() => setComposing(false)} onCreated={(t) => { setComposing(false); load(); setActive(t.id); }} />
        ) : (
          <div className="mt-6 space-y-3">
            <button onClick={() => setComposing(true)} className="btn-primary" data-testid="support-new">Nouveau message</button>
            {tickets === null ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500" /> :
              tickets.length === 0 ? <p className="text-sm text-zinc-500 pt-4">Aucune conversation pour le moment.</p> :
                tickets.map((t) => (
                  <button key={t.id} onClick={() => setActive(t.id)} className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-4" data-testid={`ticket-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <p className="font-bold truncate flex-1">{t.subject}</p>
                      {t.unread && <span className="w-2.5 h-2.5 rounded-full bg-[#FF0050]" aria-label="Nouvelle réponse" />}
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400">{STATUS[t.status] || t.status}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate">{t.last_message}</p>
                    <p className="text-[10px] text-zinc-600 mt-1">{timeAgo(t.updated_at)}</p>
                  </button>
                ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactForm() {
  const [f, setF] = useState({ email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/support/contact", f); setSent(true); }
    catch (e2) { toast.error(!e2.response ? "Le serveur met du temps à répondre. Réessaie." : formatApiError(e2.response.data?.detail)); }
    finally { setBusy(false); }
  };
  if (sent) return (
    <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 text-sm text-emerald-200" data-testid="contact-sent">
      Message envoyé. L'équipe te répondra à l'adresse {f.email}.
    </div>
  );
  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="contact-form">
      <p className="text-xs text-zinc-500">Tu n'es pas connecté. Indique une adresse e-mail où nous pouvons te répondre.</p>
      <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Ton adresse e-mail" className="input-base" data-testid="contact-email" />
      <input required maxLength={120} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Sujet" className="input-base" data-testid="contact-subject" />
      <textarea required maxLength={2000} rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Explique-nous ton problème (ne donne jamais ton mot de passe)" className="input-base" data-testid="contact-message" />
      <button disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="contact-send">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer</button>
    </form>
  );
}

function NewTicket({ onCancel, onCreated }) {
  const [f, setF] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/support/tickets", f); onCreated(data); }
    catch (e2) { toast.error(!e2.response ? "Le serveur met du temps à répondre. Réessaie." : formatApiError(e2.response.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="ticket-form">
      <input required maxLength={120} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Sujet" className="input-base" data-testid="ticket-subject" />
      <textarea required maxLength={2000} rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Décris ton problème (ne donne jamais ton mot de passe)" className="input-base" data-testid="ticket-message" />
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
        <button disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="ticket-send">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer</button>
      </div>
    </form>
  );
}

function Thread({ id, onBack }) {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/support/tickets/${id}`); setT(data); } catch { setT(false); }
  };
  useEffect(() => { load(); }, [id]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/support/tickets/${id}/messages`, { text }); setText(""); await load(); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };
  if (t === null) return <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mt-6" />;
  if (t === false) return <p className="mt-6 text-zinc-500 text-sm">Conversation introuvable.</p>;
  return (
    <div className="mt-6" data-testid="ticket-thread">
      <button onClick={onBack} className="text-sm text-zinc-400 hover:text-white mb-3">← Mes conversations</button>
      <h2 className="text-xl font-black">{t.subject} <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-2">{STATUS[t.status] || t.status}</span></h2>
      <div className="mt-4 space-y-3">
        {t.messages.map((m) => (
          <div key={m.id} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.from === "admin" ? "bg-[#FF0050]/15 border border-[#FF0050]/20" : "bg-white/5 border border-white/10 ml-auto"}`}>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{m.from === "admin" ? "Équipe Metazik" : "Moi"} · {timeAgo(m.at)}</p>
            {m.text}
          </div>
        ))}
      </div>
      <form onSubmit={send} className="mt-5 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Ta réponse…" className="input-base flex-1" data-testid="ticket-reply" />
        <button disabled={busy} className="btn-primary px-4" aria-label="Envoyer" data-testid="ticket-reply-send"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
